// Chemin: C:\gestion-copro\src\components\documents\DocumentAffaireGroup.tsx
// Session 64 : Composant vue par affaire pour la GED

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { CategorieDocument } from '../../types/document.types';
import { getTypeDocumentLabel, getTypeColor, getCategorieLabel } from '../../services/document.service';

// Interface document simplifiée pour l'affichage
interface DocumentListItem {
  id: string;
  titre: string;
  type: string;
  categorie?: CategorieDocument;
  dateDocument: any;
  fichierType: string;
  visibilite?: 'cs_only' | 'public';
  affaireId?: string;
}

// Interface affaire
interface Affaire {
  id: string;
  titre: string;
  statut: string;
  priorite: string;
}

interface DocumentAffaireGroupProps {
  documents: DocumentListItem[];
  coproprieteId: string;
}

// Icône selon le type de fichier
const getFileIcon = (fichierType: string) => {
  if (fichierType?.includes('pdf')) {
    return (
      <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
      </svg>
    );
  }
  if (fichierType?.includes('image') || fichierType?.includes('jpg') || fichierType?.includes('png')) {
    return (
      <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
      </svg>
    );
  }
  if (fichierType?.includes('word') || fichierType?.includes('document')) {
    return (
      <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
      </svg>
    );
  }
  if (fichierType?.includes('sheet') || fichierType?.includes('excel')) {
    return (
      <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
      </svg>
    );
  }
  return (
    <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
    </svg>
  );
};

// Formater la date
const formatDate = (timestamp: any): string => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Badge statut affaire
const getStatutBadge = (statut: string) => {
  const styles: Record<string, string> = {
    'en_cours': 'bg-blue-100 text-blue-700',
    'en_attente': 'bg-amber-100 text-amber-700',
    'resolu': 'bg-green-100 text-green-700',
    'clos': 'bg-gray-100 text-gray-700'
  };
  const labels: Record<string, string> = {
    'en_cours': 'En cours',
    'en_attente': 'En attente',
    'resolu': 'Résolu',
    'clos': 'Clos'
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[statut] || 'bg-gray-100 text-gray-700'}`}>
      {labels[statut] || statut}
    </span>
  );
};

// Icône de visibilité
const VisibilityBadge = ({ visibilite }: { visibilite?: 'cs_only' | 'public' }) => {
  if (visibilite === 'public') {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700" title="Visible par les copropriétaires">
        <svg className="w-3 h-3 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
        Public
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700" title="Réservé au Conseil Syndical">
      <svg className="w-3 h-3 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
      CS
    </span>
  );
};

export default function DocumentAffaireGroup({ documents, coproprieteId }: DocumentAffaireGroupProps) {
  const [affaires, setAffaires] = useState<Affaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [openAffaires, setOpenAffaires] = useState<Set<string>>(new Set());

  // Charger les affaires
  useEffect(() => {
    const fetchAffaires = async () => {
      if (!coproprieteId) {
        setLoading(false);
        return;
      }

      try {
        const affairesQuery = query(
          collection(db, 'affaires'),
          where('coproprieteId', '==', coproprieteId),
          orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(affairesQuery);
        const affairesData = snapshot.docs.map(doc => ({
          id: doc.id,
          titre: doc.data().titre,
          statut: doc.data().statut,
          priorite: doc.data().priorite
        })) as Affaire[];
        setAffaires(affairesData);
      } catch (err) {
        console.error('Erreur chargement affaires:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAffaires();
  }, [coproprieteId]);

  // Grouper les documents par affaire
  const documentsByAffaire: Record<string, DocumentListItem[]> = {};
  const documentsNonLies: DocumentListItem[] = [];

  documents.forEach(doc => {
    if (doc.affaireId) {
      if (!documentsByAffaire[doc.affaireId]) {
        documentsByAffaire[doc.affaireId] = [];
      }
      documentsByAffaire[doc.affaireId].push(doc);
    } else {
      documentsNonLies.push(doc);
    }
  });

  // Affaires avec documents
  const affairesAvecDocs = affaires.filter(a => documentsByAffaire[a.id]?.length > 0);

  // Basculer l'état d'une affaire
  const toggleAffaire = (affaireId: string) => {
    setOpenAffaires(prev => {
      const next = new Set(prev);
      if (next.has(affaireId)) {
        next.delete(affaireId);
      } else {
        next.add(affaireId);
      }
      return next;
    });
  };

  // Tout ouvrir / tout fermer
  const hasContent = affairesAvecDocs.length > 0 || documentsNonLies.length > 0;
  const allIds = [...affairesAvecDocs.map(a => a.id), 'non-lies'];
  const allOpen = allIds.every(id => openAffaires.has(id));

  const toggleAll = () => {
    if (allOpen) {
      setOpenAffaires(new Set());
    } else {
      setOpenAffaires(new Set(allIds));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Bouton tout ouvrir/fermer */}
      {hasContent && (
        <div className="flex justify-end mb-2">
          <button
            onClick={toggleAll}
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            {allOpen ? (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
                Tout replier
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                Tout déplier
              </>
            )}
          </button>
        </div>
      )}

      {/* Affaires avec documents */}
      {affairesAvecDocs.map(affaire => {
        const affaireDocs = documentsByAffaire[affaire.id] || [];
        const count = affaireDocs.length;
        const isOpen = openAffaires.has(affaire.id);

        return (
          <div key={affaire.id} className="border rounded-lg overflow-hidden">
            {/* En-tête affaire */}
            <button
              onClick={() => toggleAffaire(affaire.id)}
              className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                {/* Icône chevron */}
                <svg 
                  className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                
                {/* Icône dossier */}
                <svg className="w-5 h-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                </svg>
                
                {/* Titre affaire */}
                <span className="font-medium text-gray-900">{affaire.titre}</span>
                
                {/* Badge statut */}
                {getStatutBadge(affaire.statut)}
              </div>

              {/* Compteur documents */}
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-700">
                  {count} doc{count > 1 ? 's' : ''}
                </span>
                {/* Lien vers affaire */}
                <Link
                  to={`/admin/affaires/${affaire.id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-blue-600 hover:text-blue-700 p-1"
                  title="Voir l'affaire"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </Link>
              </div>
            </button>

            {/* Liste des documents de l'affaire */}
            {isOpen && (
              <div className="border-t bg-gray-50">
                <ul className="divide-y divide-gray-200">
                  {affaireDocs.map(doc => (
                    <li key={doc.id}>
                      <Link
                        to={`/admin/documents/${doc.id}`}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-white transition-colors"
                      >
                        {/* Icône fichier */}
                        {getFileIcon(doc.fichierType)}

                        {/* Infos document */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 truncate">
                              {doc.titre}
                            </span>
                            <VisibilityBadge visibilite={doc.visibilite} />
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            {/* Badge type */}
                            <span className={`inline-block px-1.5 py-0.5 rounded text-xs ${getTypeColor(doc.type)}`}>
                              {getTypeDocumentLabel(doc.type)}
                            </span>
                            {/* Badge catégorie */}
                            {doc.categorie && (
                              <span className="text-xs text-gray-500">
                                • {getCategorieLabel(doc.categorie)}
                              </span>
                            )}
                            {/* Date */}
                            {formatDate(doc.dateDocument) && (
                              <span>• {formatDate(doc.dateDocument)}</span>
                            )}
                          </div>
                        </div>

                        {/* Chevron vers détail */}
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      })}

      {/* Documents non liés à une affaire */}
      {documentsNonLies.length > 0 && (
        <div className="border rounded-lg overflow-hidden border-dashed">
          {/* En-tête */}
          <button
            onClick={() => toggleAffaire('non-lies')}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
          >
            <div className="flex items-center gap-3">
              {/* Icône chevron */}
              <svg 
                className={`w-5 h-5 text-gray-400 transition-transform ${openAffaires.has('non-lies') ? 'rotate-90' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              
              {/* Icône */}
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              
              {/* Titre */}
              <span className="font-medium text-gray-600">Documents non liés à une affaire</span>
            </div>

            {/* Compteur */}
            <span className="px-2.5 py-0.5 rounded-full text-sm font-medium bg-gray-200 text-gray-600">
              {documentsNonLies.length}
            </span>
          </button>

          {/* Liste */}
          {openAffaires.has('non-lies') && (
            <div className="border-t bg-white">
              <ul className="divide-y divide-gray-200">
                {documentsNonLies.map(doc => (
                  <li key={doc.id}>
                    <Link
                      to={`/admin/documents/${doc.id}`}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                    >
                      {/* Icône fichier */}
                      {getFileIcon(doc.fichierType)}

                      {/* Infos document */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 truncate">
                            {doc.titre}
                          </span>
                          <VisibilityBadge visibilite={doc.visibilite} />
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          {/* Badge type */}
                          <span className={`inline-block px-1.5 py-0.5 rounded text-xs ${getTypeColor(doc.type)}`}>
                            {getTypeDocumentLabel(doc.type)}
                          </span>
                          {/* Badge catégorie */}
                          {doc.categorie && (
                            <span className="text-xs text-gray-500">
                              • {getCategorieLabel(doc.categorie)}
                            </span>
                          )}
                          {/* Date */}
                          {formatDate(doc.dateDocument) && (
                            <span>• {formatDate(doc.dateDocument)}</span>
                          )}
                        </div>
                      </div>

                      {/* Chevron vers détail */}
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Message si aucun document */}
      {!hasContent && (
        <div className="text-center py-8 text-gray-500">
          <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
          </svg>
          <p>Aucun document dans la GED</p>
          <p className="text-sm">Utilisez le bouton "Ajouter un document" pour commencer</p>
        </div>
      )}
    </div>
  );
}
